import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "antd";
import { Shield, ArrowLeft } from "lucide-react";
import { twoFactorService } from "../services/twoFactor.service";
import { useAuth } from "../contexts/AuthContext";
import pmLogo from "../assets/login/Logo.png";
import bannerImage from "../assets/login/banner.png";
import styles from "./TwoFactorVerificationPage.module.css";

const TwoFactorVerificationPage: React.FC = () => {
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = parseInt(searchParams.get("userId") || "0");
  const { refreshUser } = useAuth();

  if (!userId) {
    navigate("/login");
    return null;
  }

  const handleTwoFactorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setTwoFactorCode(value);
  };

  const handleBackupCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().slice(0, 8);
    setBackupCode(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const code = useBackupCode ? backupCode : twoFactorCode;
    if (!code || (useBackupCode ? code.length !== 8 : code.length !== 6)) {
      message.error(
        useBackupCode
          ? "Vui lòng nhập mã dự phòng 8 ký tự"
          : "Vui lòng nhập mã 6 số"
      );
      return;
    }

    setLoading(true);
    try {
      const result = await twoFactorService.verifyTwoFactorLogin(userId, code);
      if (result.success && result.data.token) {
        localStorage.setItem("auth_token", result.data.token);
        localStorage.setItem("auth_user_data", JSON.stringify(result.data.user));
        refreshUser();
        message.success("Đăng nhập thành công!");
        navigate("/services", { replace: true });
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          "Mã xác thực không đúng. Vui lòng thử lại."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.leftColumn}>
        <div className={styles.bannerWrapper}>
          <img
            src={bannerImage}
            alt="Nền tảng quản lý doanh nghiệp toàn diện"
            className={styles.bannerImage}
          />
        </div>
      </div>

      <div className={styles.rightColumn}>
        <div className={styles.rightCard}>
          <button
            type="button"
            onClick={() => navigate("/login")}
            className={styles.backButton}
          >
            <ArrowLeft size={18} />
            <span>Về đăng nhập</span>
          </button>

          <div className={styles.formContainer}>
            <img src={pmLogo} alt="Logo" className={styles.logo} />

            <div className={styles.header}>
              <Shield size={40} className={styles.icon} />
              <h2 className={styles.title}>Xác Thực 2FA</h2>
              <p className={styles.description}>
                Vui lòng nhập mã xác thực để hoàn tất đăng nhập
              </p>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              {!useBackupCode ? (
                <>
                  <div className={styles.inputGroup}>
                    <label className={styles.label}>Mã 2FA</label>
                    <input
                      type="text"
                      value={twoFactorCode}
                      onChange={handleTwoFactorChange}
                      placeholder="000000"
                      className={styles.input}
                      maxLength={6}
                      disabled={loading}
                      autoFocus
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setUseBackupCode(true)}
                    className={styles.backupLink}
                  >
                    Sử dụng mã dự phòng
                  </button>
                </>
              ) : (
                <>
                  <div className={styles.inputGroup}>
                    <label className={styles.label}>Mã dự phòng</label>
                    <input
                      type="text"
                      value={backupCode}
                      onChange={handleBackupCodeChange}
                      placeholder="XXXXXXXX"
                      className={styles.input}
                      maxLength={8}
                      disabled={loading}
                      autoFocus
                      style={{ letterSpacing: "2px" }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setUseBackupCode(false);
                      setBackupCode("");
                    }}
                    className={styles.backupLink}
                  >
                    Quay lại dùng mã 2FA
                  </button>
                </>
              )}

              <button
                type="submit"
                disabled={
                  loading ||
                  (useBackupCode
                    ? backupCode.length !== 8
                    : twoFactorCode.length !== 6)
                }
                className={styles.submitButton}
              >
                {loading ? "Đang xác thực..." : "Xác Thực"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorVerificationPage;
